import { Component, ElementRef, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { filter, fromEvent, Subscription, take } from 'rxjs';
import { ContextMenuItemEvent } from '../models/context-menu-item-event';
import { MultiLevelContextMenuItem } from '../models/multi-level-context-menu-item';

/**
 * Représente un menu contextuel possédant plusieurs niveaux de sous-menus appellé noeud.
 * Chacun des sous-niveaux (noeud) est représenté par une instance de ce composant.
 * Le noeud racine est caractérisé par la propriété isRootNode à true.
 */
@Component({
    selector: 'app-multi-level-context-menu',
    imports: [MatMenuModule],
    templateUrl: './multi-level-context-menu.component.html',
    styleUrl: './multi-level-context-menu.component.scss'
})
export class MultiLevelContextMenuComponent implements OnDestroy {
    /**
     * Données du noeud courant.
     */
    @Input() nodeData: MultiLevelContextMenuItem = null;

    /**
     * Valeur indiquant s'il s'agit du noeud racine.
     */
    @Input() isRootNode = false;

    /**
     * Postion du menu contextuel.
     */
    contextMenuPosition = { x: '0px', y: '0px' };

    /**
     * Valeur permettant d'identifier quelle est la source du menu contexte.
     * Utilisé lorsque la même instance du menu contextuel est utilisée par plusieurs sources.
     */
    source: string;

    /**
     * Retourne une reference sur l'élément HTML contenant le menu contextuel.
     */
    private get element() {
        return this.host.nativeElement;
    }

    /**
     * Retourne une reference sur le composant angular qui déclenche l'affichage du menu contextuel.
     */
    @ViewChild(MatMenuTrigger) contextMenuTrigger: MatMenuTrigger | null = null;

    /**
     * Reference à l'abonnement aux evenements de clic souris en dehors du menu contextuel.
     */
    clickOutside$: Subscription;

    /**
     * Emis lorsque l'utilisateur clique sur un noeud enfant du menu contextuel.
     */
    @Output()
    // eslint-disable-next-line @angular-eslint/no-output-on-prefix
    onContextMenuItemClick: EventEmitter<ContextMenuItemEvent> = new EventEmitter<ContextMenuItemEvent>();

    /**
     * Constructeur
     * @param host : Référence au composant.
     */
    constructor(private host: ElementRef) {
        // Abonnement aux evenements de clic souris en dehors du menu contextuel pour fermer le menu contextuel.
        this.clickOutside$ = fromEvent<MouseEvent>(document, 'click')
            .pipe(
                filter((event) => {
                    const clickTarget = event.target as HTMLElement;
                    return !!this.element && !this.element.contains(clickTarget);
                }),
                take(1)
            )
            .subscribe(() => this.close());
    }

    /**
     * Ouvre le menu contextuel.
     * @param source : Valeur permettant d'identifier quelle est la source du menu contexte.
     * @param event : Evenement du clic de la souris.
     */
    public openMenu(source: string, event: MouseEvent) {
        this.source = source;
        this.contextMenuPosition.x = event.clientX - 5 + 'px';
        this.contextMenuPosition.y = event.clientY - 10 + 'px';
        if (this.contextMenuTrigger) {
            this.contextMenuTrigger.openMenu();
        }
    }

    /**
     * Appelée à la destruction du composant.
     */
    ngOnDestroy(): void {
        // Désabonnement aux evenements de clic souris en dehors du menu contextuel.
        this.clickOutside$ && this.clickOutside$.unsubscribe();
    }

    /**
     * Ferme le menu contextuel.
     */
    private close() {
        // Désabonnement aux evenements de clic souris en dehors du menu contextuel.
        this.clickOutside$ && this.clickOutside$.unsubscribe();
    }

    /**
     * Levé lorsque l'utilisateur clique sur un élément enfant du menu contextuel.
     * @param event : Evenement clic de la souris
     * @param data : Données du noeud cliqué.
     */
    onContextMenuClick(event: MouseEvent, data: MultiLevelContextMenuItem): any {
        this.onContextMenuItemClick.emit(new ContextMenuItemEvent(data.data, this.source));
    }

    /**
     * Transmet l'evenement émis par un noeud enfant au noeud parent.
     * @param event : Evenement indiquant qu'un noeud enfant a été cliqué.
     */
    forwardContextMenuItemClick(event: ContextMenuItemEvent) {
        event.source = this.source;
        this.onContextMenuItemClick.emit(event);
    }
}
